import { PropsWithChildren, useCallback, useRef } from 'react';
import {
  AppointmentProposalOptionSOW,
  AppointmentProposalOptionSOWCreate,
  AppointmentProposalOptionSOWsContext,
  AppointmentProposalOptionSOWsProvider,
  useAppointmentProposalOptionSOWs,
} from '../../../data-access/appointmentProposalOptionSows';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import { createOfflineUUID } from '../../rxdb/utils/rxDbOperations';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { useEntityReplication } from '../../rxdb/utils/useEntityReplication';

const buildOfflineSOW = (
  appointmentId: string,
  appointmentProposalId: string,
  appointmentProposalOptionId: string,
  create: AppointmentProposalOptionSOWCreate
): AppointmentProposalOptionSOW => ({
  appointmentProposalOptionSOWId: createOfflineUUID(),
  appointmentId,
  appointmentProposalId,
  appointmentProposalOptionId,
  entityType: 'APPOINTMENT_PROPOSAL_OPTION_SOW',
  status: 'ACTIVE',
  sowId: create.sowId,
  jobId: create.jobId,
  shortDescription: create.shortDescription,
  sortOrder: create.sortOrder,
  summary: create.summary,
  title: create.title,
});

const OfflineAppointmentProposalOptionSowsOverride: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const context = useAppointmentProposalOptionSOWs();
  const data = useOfflineDbData();
  const { upsertSOWs, selectProposalSOWs, setSOWsMutating, clearSOWsMutating } =
    context;

  const lastProposalIdRef = useRef<string | null>(null);

  const syncSOWsInternal = useCallback(
    async (appointmentProposalId: string) => {
      const items = await data.find('appointment_proposal_option_sows', {
        selector: { appointmentProposalId },
      });
      upsertSOWs(items);
      return items;
    },
    [data, upsertSOWs]
  );

  const onSync = useCallback(async () => {
    if (lastProposalIdRef.current) {
      await syncSOWsInternal(lastProposalIdRef.current);
    }
  }, [syncSOWsInternal]);

  const { initError } = useEntityReplication(
    'appointment_proposal_option_sows',
    'airship-appointment-proposal-option-sows',
    { onSync, upsert: upsertSOWs }
  );

  const appendSOWs: typeof context.appendSOWs = useCallback(
    (sows) => data.bulkUpsertSynced('appointment_proposal_option_sows', sows),
    [data]
  );

  const fetchAppointmentProposalSOWs: typeof context.fetchAppointmentProposalSOWs =
    useCallback(
      async (_appointmentId, appointmentProposalId) => {
        lastProposalIdRef.current = appointmentProposalId;
        selectProposalSOWs(appointmentProposalId);
        return syncSOWsInternal(appointmentProposalId);
      },
      [selectProposalSOWs, syncSOWsInternal]
    );

  const createAppointmentProposalOptionSOW: typeof context.createAppointmentProposalOptionSOW =
    useCallback(
      (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        create
      ) => {
        setSOWsMutating({ creatingIds: [create.sowId] });
        return data
          .insertSynced(
            'appointment_proposal_option_sows',
            buildOfflineSOW(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              create
            ),
            0
          )
          .then((result) => {
            upsertSOWs([result]);
            return result;
          })
          .catch((error) => {
            clearSOWsMutating({
              creatingIds: [create.sowId],
              error:
                'We were unable to add the statement of work. Please try again.',
            });
            throw error;
          });
      },
      [data, setSOWsMutating, clearSOWsMutating, upsertSOWs]
    );

  const createManyAppointmentProposalOptionSOWs: typeof context.createManyAppointmentProposalOptionSOWs =
    useCallback(
      (appointmentId, appointmentProposalId, payloads) => {
        const creatingIds = payloads.map(({ sow }) => sow.sowId);
        setSOWsMutating({ creatingIds });
        return data
          .bulkInsertSynced(
            'appointment_proposal_option_sows',
            payloads.map(({ appointmentProposalOptionId, sow }) =>
              buildOfflineSOW(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                sow
              )
            ),
            0
          )
          .then((results) => {
            upsertSOWs(results);
            return results;
          })
          .catch((error) => {
            clearSOWsMutating({
              creatingIds,
              error:
                'We were unable to add the statements of work. Please try again.',
            });
            throw error;
          });
      },
      [data, setSOWsMutating, clearSOWsMutating, upsertSOWs]
    );

  const updateAppointmentProposalOptionSOW: typeof context.updateAppointmentProposalOptionSOW =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        _appointmentProposalOptionId,
        appointmentProposalOptionSOWId,
        update
      ) => {
        setSOWsMutating({});
        return data
          .patchSynced(
            'appointment_proposal_option_sows',
            appointmentProposalOptionSOWId,
            { ...update, offlineSyncErrors: null },
            0
          )
          .then((result) => {
            upsertSOWs([result]);
            return result;
          })
          .catch((error) => {
            clearSOWsMutating({
              error:
                'We were unable to save your statement of work changes. Please try again.',
            });
            throw error;
          });
      },
      [data, setSOWsMutating, clearSOWsMutating, upsertSOWs]
    );

  const deleteAppointmentProposalOptionSOW: typeof context.deleteAppointmentProposalOptionSOW =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        _appointmentProposalOptionId,
        appointmentProposalOptionSOWId
      ) => {
        setSOWsMutating({});
        return data
          .softDeleteSynced(
            'appointment_proposal_option_sows',
            appointmentProposalOptionSOWId,
            0
          )
          .then((doc) => upsertSOWs([doc]))
          .catch((error) => {
            clearSOWsMutating({
              error:
                'We were unable to remove the statement of work. Please try again.',
            });
            throw error;
          });
      },
      [data, setSOWsMutating, clearSOWsMutating, upsertSOWs]
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="appointment proposal option SOWs"
      />
    );
  }

  return (
    <AppointmentProposalOptionSOWsContext.Provider
      value={{
        ...context,
        appendSOWs,
        fetchAppointmentProposalSOWs,
        createAppointmentProposalOptionSOW,
        createManyAppointmentProposalOptionSOWs,
        updateAppointmentProposalOptionSOW,
        deleteAppointmentProposalOptionSOW,
      }}
    >
      {children}
    </AppointmentProposalOptionSOWsContext.Provider>
  );
};

export const OfflineAppointmentProposalOptionSowsProvider: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <AppointmentProposalOptionSOWsProvider>
    <OfflineAppointmentProposalOptionSowsOverride>
      {children}
    </OfflineAppointmentProposalOptionSowsOverride>
  </AppointmentProposalOptionSOWsProvider>
);
